/*
 * (c) Copyright, 2021, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useEffect, useState } from 'react'
import { Button, FormGroup, FormLabel } from 'react-bootstrap'
import { Formik } from 'formik'
import Utils from '~/lib/Utils'
import Modal from '~/components/Modal'
import Loader from '~/components/shared/Loader'
import { Select } from '~/components/Select'
import CustomErrorMessage from '~/components/ErrorMessage'
import _ from 'lodash'
import { getDetails, rejectFile } from '~/services/api/files'
import { documentTypesApiService } from '~/services/api/api.documenttypes'
import { getRejectionReasons } from '~/services/api/api.settings'
import { toast } from '~/services/toast'
import { useServiceContext } from '~/root'
import { useTranslation } from 'react-i18next'
import { useEsignature } from '~/hooks/useEsignature'
import { TextArea } from '@mdsol/lego'

interface RejectDocumentsModalProps {
  fsiId: number
  params?: {
    selectedIds?: number[]
    [key: string]: any
  }
  onClose?: () => void
}

interface FileType {
  id?: number
  name?: string
  fsiTypeTemplate?: { id: number }
  [key: string]: any
}

interface DocType {
  requireRejectReason?: boolean
  [key: string]: any
}

interface ReasonType {
  id: number
  text: string
}

interface OptionType {
  value: number
  label: string
  [key: string]: any
}

interface FormValues {
  rejectReasonId: number | null
  customRejectReasonText: string
}

const RejectDocumentsModal: React.FC<RejectDocumentsModalProps> = ({ fsiId, params, onClose }) => {
  const context = useServiceContext()
  const { t: translate } = useTranslation()
  const { signAction } = useEsignature()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [file, setFile] = useState<FileType>({})
  const [docType, setDocType] = useState<DocType>({})
  const [availableReasons, setAvailableReasons] = useState<OptionType[]>([])

  useEffect(() => {
    setIsOpen(true)
  }, [])

  useEffect(() => {
    getDetails(fsiId).then(function (data: unknown) {
      const fileData = data as FileType
      setFile(fileData)
      if (fileData.fsiTypeTemplate?.id) {
        documentTypesApiService.getById(fileData.fsiTypeTemplate.id).then(function (docType: unknown) {
          setDocType(docType as DocType)
        })
      }
    })
    getRejectionReasons().then(function (data: unknown) {
      const reasonsData = data as { elements: ReasonType[] }
      const avlReasons = reasonsData.elements ?? []
      avlReasons.push({ id: 0, text: translate('CUSTOM...') })
      setAvailableReasons(Utils.transformToSelectOptions(avlReasons, 'id', 'text') as OptionType[])
    })
  }, [])

  function isMultiple(): boolean {
    return !!((params?.selectedIds?.length ?? 0) > 1)
  }

  function save(values: FormValues) {
    const okFn = function (passModal?: any, esignature?: any) {
      const requestObj: any = {}
      if (esignature) {
        requestObj.esignature = esignature
      }
      _.extend(requestObj, values)
      const apiCall = rejectFile(isMultiple() ? params!.selectedIds! : file.id!, requestObj)
      apiCall.then(
        function () {
          doClose()
          window.dispatchEvent(new Event('updateTables'))
          window.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))
          window.dispatchEvent(new CustomEvent('updateFiles', { detail: true }))
          window.dispatchEvent(new CustomEvent('fileupdate:details', { detail: file.id }))
        },
        function (data: any) {
          if (data && data.data && 'message' in data.data) {
            toast.error(data.data.message)
          }
        }
      )
      return apiCall
    }

    if (context.service.auth.requireESignatureForFileCommands()) {
      return signAction('ADMIN_ACTION', 'BROWSER_ACTION_REJECT_DOC').then((validation) => {
        return okFn(undefined, validation)
      })
    } else {
      return okFn()
    }
  }

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  return (
    <>
      <Modal
        className="theme-sandman"
        title={translate('REJECT_FILE_H_TITLE')}
        size="md"
        show={isOpen}
        handleClose={doClose}
      >
        <Loader blocking={loading}>
          <Formik<FormValues>
            initialValues={{
              rejectReasonId: null,
              customRejectReasonText: ''
            }}
            validate={(values: FormValues) => {
              const errors: Partial<Record<keyof FormValues, string>> = {}
              if (isMultiple() || (!isMultiple() && docType?.requireRejectReason)) {
                if (_.isNull(values.rejectReasonId)) {
                  errors.rejectReasonId = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
                }
                if (values.rejectReasonId === 0 && _.isEmpty(values?.customRejectReasonText)) {
                  errors.customRejectReasonText = 'GENERAL_FORM_FIELD_IS_REQUIRED_ERROR'
                }
              }
              return errors
            }}
            onSubmit={(values: FormValues) => {
              save(values)
            }}
          >
            {({ setFieldValue, values, handleSubmit }) => (
              <>
                <Modal.Body>
                  <FormGroup className="form-group md-form-group wparag">
                    <label>{translate('DOCUMENT')}</label>
                    <p>{isMultiple() ? translate('MULTIPLE_DOCS') : (file?.name ?? '')}</p>
                  </FormGroup>
                  <FormGroup className="wparag w50p" controlId="3e9732a9-5ca4-471a-8c08-0cdcc44a6420">
                    <FormLabel>{translate('TASK_REJECTION_REASON_LABEL')}</FormLabel>
                    <Select
                      name="rejectReasonId"
                      placeholder={translate('SELECT_REJECTION_REASON_LABEL')}
                      options={availableReasons}
                      value={values.rejectReasonId}
                      onChange={(option) => {
                        if (option && 'value' in option) {
                          setFieldValue('rejectReasonId', option.value)
                        } else {
                          setFieldValue('rejectReasonId', null)
                        }
                      }}
                      searchable
                    />
                    <CustomErrorMessage name="rejectReasonId" />
                  </FormGroup>
                  <FormGroup
                    style={{ marginTop: '32px' }}
                    className="wparag w50p mt-5 mb5"
                    controlId="3e9732a9-5ca4-471a-8c08-0cdcc44a6420"
                  >
                    <FormLabel>{translate('COMMENT')}</FormLabel>
                    <TextArea
                      maxLength={254}
                      name="customRejectReasonText"
                      onChange={(e: any) => {
                        setFieldValue('customRejectReasonText', e.target.value)
                      }}
                      id="a030c4ad-b4ae-4e4c-b86c-110a237cb24a"
                      value={values?.customRejectReasonText}
                      rows={6}
                      formatCounter={(length: any, max: any) =>
                        Utils.formatTextAreaCounter(length, max, translate('CHAR_COUNT_LIMIT'))
                      }
                    />
                    <CustomErrorMessage name="customRejectReasonText" />
                  </FormGroup>
                </Modal.Body>
                <Modal.Footer>
                  <button
                    data-action={'cancel'}
                    className="btn btn-secondary text-capitalize mr-2 btn-link"
                    onClick={doClose}
                    id="fc29d04b-3fe6-4ee0-9581-ea0c670cf54c"
                  >
                    {translate('CANCEL')}
                  </button>
                  <button
                    data-action={'save'}
                    className="btn btn-primary text-capitalize"
                    onClick={handleSubmit as any}
                    id="be78d793-f56c-4cff-89cc-bb8ff47785e3"
                  >
                    {translate('REJECT')}
                  </button>
                </Modal.Footer>
              </>
            )}
          </Formik>
        </Loader>
      </Modal>
    </>
  )
}

export default RejectDocumentsModal
